import { createZoomImageWheel, ZoomImageWheelState, ZoomImageWheelStateUpdate } from '@zoom-image/core'
import { loadDeferredImages, unloadDeferredImages } from '@/components/helpers/deferred-images'
import { VisibilityElement } from './visibility'

const SLIDE_WINDOW = 1

const RELEASE_DELAY = 2000

interface Selectors {
  readonly zoomContainer: string;
  readonly container: string;
  readonly buttons: string;
  readonly globalTriggers: string;
  readonly close: string;
  readonly scrollValue: string;
}

interface ScrollBehavior {
  readonly default: ScrollIntoViewOptions;
  readonly none: ScrollIntoViewOptions;
}

interface GalleryActions {
  readonly next: 'next';
  readonly prev: 'prev';
}

type GalleryAction = GalleryActions[keyof GalleryActions];

interface StateChangePayload {
  index?: string;
  action?: GalleryAction;
  scrollBehavior?: keyof ScrollBehavior;
  images?: NodeListOf<Element>;
}

interface ZoomState {
  currentZoom: number;
  [key: string]: unknown;
}

// Missing type definitions for @zoom-image/core
type StoreListener<TState> = (arg: {
  state: TState;
  updatedProperties: Partial<TState>;
}) => void;

// Missing type definitions for @zoom-image/core
interface ZoomContainerReference {
  cleanup(): void;
  subscribe: (listener: StoreListener<ZoomImageWheelState>) => () => void;
  setState: (newState: ZoomImageWheelStateUpdate) => void;
  getState: () => ZoomImageWheelState;
}

/**
 * LightboxGallery - Custom Element
 * Extended from VisibilityElement.
 *
 * Usage:
 * 1. Wrap modal elements with <x-lighthouse-gallery>
 * 2. Set data-in-view attribute to the index of the element in view
 * 3. Add data-role="lighthouse-trigger" to trigger elements
 */
class LightBoxElement extends VisibilityElement {
  private readonly selectors: Selectors
  private readonly scrollBehavior: ScrollBehavior
  private readonly galleryActions: GalleryActions
  private readonly zoomContainerReferences: ZoomContainerReference[]
  private readonly galleryImages: NodeListOf<Element>
  private zoomTeardowns: Array<() => void> = []
  private zoomInitialised = false
  private isLightboxOpen = false
  private wrapTimer: number | null = null
  private releaseTimer: number | null = null

  constructor() {
    super()

    this.selectors = {
      zoomContainer: 'div[data-role="zoom_image_container"]',
      container: 'div[data-role="gallery_image_container"]',
      buttons: 'button[data-role="gallery-button"]',
      globalTriggers: 'div[data-role="lightbox-trigger"]',
      close: '[data-role="close"]',
      scrollValue: '[data-role="scroll-value"]'
    } as const

    this.scrollBehavior = {
      default: { behavior: 'smooth', inline: 'start' },
      none: {}
    } as const

    this.galleryActions = {
      next: 'next',
      prev: 'prev'
    } as const

    this.zoomContainerReferences = []
    this.galleryImages = this.querySelectorAll(this.selectors.container)

    const originalHandleShow = this.handleShow
    this.handleShow = () => {
      originalHandleShow.call(this)
      this.isLightboxOpen = true
      this.updateSlideWindow()
    }

    const originalHandleHide = this.handleHide
    this.handleHide = () => {
      originalHandleHide.call(this)
      this.isLightboxOpen = false
      this.clearWrapTimer()
      this.releaseSlides()
    }

    this.handleStateChange()
  }

  /**
   * Keeps only the active slide and its immediate neighbours decoded. A full product
   * gallery held open at once is the single largest image cost on a PDP, and it was
   * being paid even while the lightbox was closed.
   */
  private updateSlideWindow(): void {
    if (!this.isLightboxOpen) return

    const keep = this.windowIndices(this.activeIndex())

    this.galleryImages.forEach((slide, index) => {
      if (keep.has(index)) loadDeferredImages(slide)
    })

    this.scheduleRelease()
  }

  /**
   * Detaching is deferred because holding an arrow button moves the window faster than
   * an image can decode. Tearing a slide down mid-flight cancels its download and, if
   * the user settles nearby, forces it to start over from nothing.
   */
  private scheduleRelease(): void {
    if (this.releaseTimer !== null) window.clearTimeout(this.releaseTimer)

    this.releaseTimer = window.setTimeout(() => {
      this.releaseTimer = null
      if (!this.isLightboxOpen) return

      const keep = this.windowIndices(this.activeIndex())
      this.galleryImages.forEach((slide, index) => {
        if (!keep.has(index)) unloadDeferredImages(slide)
      })
    }, RELEASE_DELAY)
  }

  private activeIndex(): number {
    return parseInt(this.dataset.inView ?? '0', 10)
  }

  /**
   * Slides to keep decoded for a given active index.
   */
  private windowIndices(activeIndex: number): Set<number> {
    const total = this.galleryImages.length
    const keep = new Set<number>()

    for (let offset = -SLIDE_WINDOW; offset <= SLIDE_WINDOW; offset += 1) {
      const index = activeIndex + offset
      if (index >= 0 && index < total) keep.add(index)
    }

    if (activeIndex <= SLIDE_WINDOW) keep.add(total - 2)
    if (activeIndex >= total - 1 - SLIDE_WINDOW) keep.add(1)

    return keep
  }

  private clearWrapTimer(): void {
    if (this.wrapTimer === null) return
    window.clearTimeout(this.wrapTimer)
    this.wrapTimer = null
  }

  private clearReleaseTimer(): void {
    if (this.releaseTimer === null) return
    window.clearTimeout(this.releaseTimer)
    this.releaseTimer = null
  }

  private releaseSlides(): void {
    this.clearReleaseTimer()
    this.galleryImages.forEach((slide) => unloadDeferredImages(slide))
  }

  handleInView = (index: string): void => {
    this.dataset.inView = index
  }

  /**
   * Handles state changes in the gallery
   * @param payload - State change configuration
   * @returns The new index after state change
   */
  private handleStateChange(payload?: StateChangePayload): number {
    // Any navigation supersedes a wrap that has not fired yet. Without this, leaving a
    // clone slide before its timer elapses lets the stale jump land a second later and
    // drag the user off whichever slide they actually chose.
    this.clearWrapTimer()

    const index = payload?.index ?? this.dataset.inView
    const images = this.galleryImages
    const parsedIndex = parseInt(index ?? '0', 10)

    if (this.zoomContainerReferences.length) {
      this.zoomContainerReferences[parsedIndex]?.setState({ currentZoom: 0 })
    }

    const newIndex = this.galleryActionsMap(payload?.action, parsedIndex, images.length)
    this.handleInView(newIndex.toString())

    // Update active state of buttons
    this.querySelectorAll<HTMLElement>(this.selectors.buttons).forEach(button => {
      const buttonIndex = parseInt(button.dataset.index ?? '0', 10)
      const action = button.dataset.action
      if (action === '1' && (newIndex === 1 || newIndex === images.length - 1 || newIndex === 0)) {
        button.dataset.active = 'true'
      } else {
        button.dataset.active = (buttonIndex === newIndex).toString()
      }
    })

    const behavior = payload?.scrollBehavior ?? 'none'
    images[newIndex]?.scrollIntoView(this.scrollBehavior[behavior])
    this.handleZoomValue(newIndex, images)
    this.updateSlideWindow()

    return newIndex
  }

  /**
   * Updates zoom value display for mobile users
   */
  private handleZoomValue(index: number, images: NodeListOf<Element>): void {
    const container = images[index]?.querySelector<HTMLElement>(this.selectors.zoomContainer)
    const currentZoom = container?.dataset.mobileZoom
    const scrollElement = document.querySelector<HTMLElement>(this.selectors.scrollValue)
    if (scrollElement && currentZoom) {
      scrollElement.innerHTML = `(${currentZoom}%)`
    }
  }

  /**
   * Handles gallery wrap-around behavior
   */
  private handleGalleryWrap(currentIndex: number, images: NodeListOf<Element>): void {
    const currentImage = images[currentIndex] as HTMLElement
    const wrap = currentImage?.dataset.type

    if (wrap) {
      const resetIndex = currentIndex >= images.length - 1 ? 1 : images.length - 2

      this.clearWrapTimer()
      this.wrapTimer = window.setTimeout(() => {
        this.wrapTimer = null;
        (images[resetIndex] as HTMLElement)?.scrollIntoView({})
        this.handleInView(resetIndex.toString())
        this.handleZoomValue(resetIndex, images)
        this.updateSlideWindow()
      }, 1000)
    }
  }

  /**
   * Handles gallery button clicks
   */
  private handleButtons = (e: Event): void => {
    const target = e.target as HTMLElement
    const button = target.closest('button[data-role="gallery-button"]') as HTMLButtonElement
    if (!button) return
    const action = button.dataset.action as GalleryAction
    const images = this.galleryImages
    const numericAction = parseInt(action as string, 10)
    if (!isNaN(numericAction)) {
      const newIndex = this.handleStateChange({ index: action, scrollBehavior: 'default', images })
      // Only handle wrap if we're at the edges
      if (newIndex === 0 || newIndex === images.length - 1) {
        this.handleGalleryWrap(newIndex, images)
      }
      return
    }
    const newIndex = this.handleStateChange({ action, scrollBehavior: 'default', images })
    // Only handle wrap if we're at the edges
    if (newIndex === 0 || newIndex === images.length - 1) {
      this.handleGalleryWrap(newIndex, images)
    }
  }

  /**
   * Handles global trigger clicks
   */
  private handleGlobalTrigger = (e: Event): void => {
    this.handleToggle()
    const image = e.currentTarget as HTMLElement
    const index = image.dataset.index
    if (index) {
      this.handleStateChange({ index, scrollBehavior: 'none' })
    }
  }

  /**
   * Closes the lightbox
   */
  private handleClose = (): void => {
    this.handleHide()
  }

  /**
   * Initializes zoom functionality for images
   */
  private handleImageZoom(): void {
    if (this.zoomInitialised) return
    this.zoomInitialised = true

    this.querySelectorAll<HTMLElement>(this.selectors.zoomContainer).forEach((el) => {
      const result = createZoomImageWheel(el)
      this.zoomContainerReferences.push(result)
      const unsubscribe = result.subscribe(({ state }: { state: ZoomState }) => {
        this.imageZoomUpdate(state, el)
      })
      this.zoomTeardowns.push(unsubscribe)

      // Double-click to zoom toggle
      const image = el.querySelector('img')
      if (image) {
        const handleDoubleClick = () => {
          const currentState = result.getState()
          const currentZoom = currentState.currentZoom
          const newZoom = currentZoom === 1 ? 2 : 1
          result.setState({ currentZoom: newZoom })
        }
        image.addEventListener('dblclick', handleDoubleClick)
        this.zoomTeardowns.push(() => image.removeEventListener('dblclick', handleDoubleClick))
      }
    })
  }

  private cleanupImageZoom(): void {
    this.zoomTeardowns.forEach((teardown) => teardown())
    this.zoomTeardowns = []

    this.zoomContainerReferences.forEach((reference) => reference.cleanup())
    this.zoomContainerReferences.length = 0

    this.zoomInitialised = false
  }

  /**
   * Updates zoom state for selected image
   */
  private imageZoomUpdate(state: ZoomState, element: HTMLElement): void {
    try {
      const scroll = Math.round(state?.currentZoom * 100)
      const scrollElement = document.querySelector<HTMLElement>(this.selectors.scrollValue)
      if (scrollElement) {
        element.dataset.mobileZoom = scroll.toString()
        scrollElement.innerHTML = `(${scroll}%)`
      }
    } catch (err) {
      console.error('Error updating image zoom:', err)
    }
  }

  /**
   * Maps gallery actions to index changes
   */
  private galleryActionsMap(
    action: GalleryAction | undefined,
    currentValue: number,
    totalImages: number
  ): number {
    switch (action) {
      case this.galleryActions.next:
        return currentValue < totalImages - 1 ? currentValue + 1 : currentValue
      case this.galleryActions.prev:
        return currentValue > 0 ? currentValue - 1 : currentValue
      default:
        return currentValue
    }
  }

  /**
   * Adds event listeners
   */
  addEventListeners(): void {
    super.addEventListeners()

    this.querySelectorAll<HTMLElement>(this.selectors.buttons)
      .forEach(el => el.addEventListener('click', this.handleButtons))

    document.querySelectorAll<HTMLElement>(this.selectors.globalTriggers)
      .forEach(el => el.addEventListener('click', this.handleGlobalTrigger))

    document.querySelectorAll<HTMLElement>(this.selectors.close)
      .forEach(el => el.addEventListener('click', this.handleClose))
  }

  /**
   * Removes event listeners
   */
  removeEventListeners(): void {
    super.removeEventListeners()

    this.querySelectorAll<HTMLElement>(this.selectors.buttons)
      .forEach(el => el.removeEventListener('click', this.handleButtons))

    document.querySelectorAll<HTMLElement>(this.selectors.globalTriggers)
      .forEach(el => el.removeEventListener('click', this.handleGlobalTrigger))

    document.querySelectorAll<HTMLElement>(this.selectors.close)
      .forEach(el => el.removeEventListener('click', this.handleClose))
  }

  connectedCallback(): void {
    super.connectedCallback()
    this.handleImageZoom()
    this.addEventListeners()
  }

  disconnectedCallback(): void {
    super.disconnectedCallback()
    this.removeEventListeners()
    this.cleanupImageZoom()
    this.clearWrapTimer()
    this.clearReleaseTimer()
    this.releaseSlides()
  }
}

export { LightBoxElement }
